import _ from "lodash";

export type ChangedField = {
    path: string;
    removed: unknown[];
    added: unknown[];
};

export type ChangedFieldsData = {
    old: Record<string, unknown> | null;
    new: Record<string, unknown>;
};

const absentFieldValue = "–";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

// The backend serializes the product's own fields in PascalCase ("AndroidVersion") but leaves the
// nested build-config keys camelCased ("rootDevice"). Lowering the first letter of the top-level
// segment lines the whole path up with the one the advanced JSON editor shows for the same setting.
const childPath = (parentPath: string, key: string) =>
    parentPath ? `${parentPath}.${key}` : key.charAt(0).toLowerCase() + key.slice(1);

// Cancels every element against at most one equal element on the other side, so a list that dropped
// one of two identical entries still reports that entry — which _.differenceWith would swallow,
// because it removes all occurrences as soon as one match exists.
const missingElements = (source: unknown[], other: unknown[]): unknown[] => {
    const unmatched = [...other];
    const missing: unknown[] = [];

    source.forEach((element) => {
        const match = unmatched.findIndex((candidate) => _.isEqual(candidate, element));

        if (match === -1) {
            missing.push(element);
        } else {
            unmatched.splice(match, 1);
        }
    });

    return missing;
};

const collectChanges = (oldValue: unknown, newValue: unknown, path: string, collected: ChangedField[]) => {
    if (_.isEqual(oldValue, newValue)) {
        return;
    }

    if (isPlainObject(oldValue) && isPlainObject(newValue)) {
        _.union(Object.keys(oldValue), Object.keys(newValue))
            .sort()
            .forEach((key) => collectChanges(oldValue[key], newValue[key], childPath(path, key), collected));
        return;
    }

    if (Array.isArray(oldValue) && Array.isArray(newValue)) {
        // Two arrays only line up index by index when nothing was inserted or removed. With equal
        // lengths, descending turns "the whole module list changed" into "modules[0].tags changed".
        if (oldValue.length === newValue.length) {
            oldValue.forEach((element, index) => collectChanges(element, newValue[index], `${path}[${index}]`, collected));
            return;
        }

        collected.push({ path, removed: missingElements(oldValue, newValue), added: missingElements(newValue, oldValue) });
        return;
    }

    collected.push({
        path,
        removed: oldValue === undefined ? [] : [oldValue],
        added: newValue === undefined ? [] : [newValue],
    });
};

export const getChangedFields = (data: ChangedFieldsData): ChangedField[] => {
    const collected: ChangedField[] = [];

    // The backend sends old: null when the product had no previous state. Comparing against an empty
    // object lists every new field on its own; passing the null through would report the whole product
    // as a single nameless change.
    collectChanges(data.old ?? {}, data.new, "", collected);
    return collected;
};

export const formatFieldValue = (value: unknown): string => {
    if (value === undefined) {
        return absentFieldValue;
    }

    return JSON.stringify(value, null, 2);
};
